/** Declared record links use the same coordinates on a card and inside its page. */
import type { RecordRead } from "../lib/api";
import { linkHref, resolveWorkspaceLink, workspaceNavigation, type WorkspaceLink } from "../studio/WorkContext";

const recordPath = /^(arguments|result|child)\./;

function coordinate(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value
    : typeof value === "number" && Number.isFinite(value) ? String(value) : undefined;
}

/** Follow only the explicitly named fields of the server's projected record. */
function atPath(read: RecordRead, path: string): unknown {
  let value: unknown = read;
  for (const key of path.split(".")) {
    if (!value || typeof value !== "object" || !Object.hasOwn(value, key)) return undefined;
    value = (value as Record<string, unknown>)[key];
  }
  return value;
}

export function recordLinkHref(declared: WorkspaceLink, read: RecordRead | undefined, context: Record<string, string>): string | undefined {
  const link = resolveWorkspaceLink(declared, context);
  if (!link) return undefined;
  if (read === undefined) return Object.values(link.context ?? {}).some(source => recordPath.test(source.trim()))
    ? undefined : linkHref(link, context);
  // An accepted background result may already name an object while work continues.
  // Failed/unknown work cannot reuse an earlier payload to claim delivery.
  if (["failed", "unknown", "stopped"].includes(read.state) || read.result?.ok === false
      || (read.state !== "done" && read.result?.ok !== true)) return undefined;
  if (!link.context) return linkHref(link, context);
  const values: Record<string, string> = {};
  for (const [key, mapped] of Object.entries(link.context)) {
    const source = mapped.trim();
    const value = coordinate(recordPath.test(source) ? atPath(read, source) : context[source]);
    // Opening a default object because one declared coordinate went missing is misleading.
    if (value === undefined) return undefined;
    values[key] = value;
  }
  return linkHref({ ...link, context: undefined }, values);
}

export function recordLinks(metadata: Record<string, unknown> | undefined, read: RecordRead, context: Record<string, string>) {
  const navigation = workspaceNavigation(metadata);
  return (navigation?.links ?? []).filter(link => link.menu !== false).flatMap(link => {
    const href = recordLinkHref(link, read, context);
    return href ? [{ label: link.label, href }] : [];
  });
}
